// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '@loa/ui-components';
import type { Client } from '../../../../types/client';
import type {
  MaxFundingPayload,
  MaxFundingScenarioInputs,
  MaxFundingSubScenario,
  SelectedPension,
} from '../../../../types/calculationResult';
import { MaxFundingScenarioTabs } from './MaxFundingScenarioTabs';
import { MaxFundingScenarioForm } from './MaxFundingScenarioForm';
import { MaxFundingResultsPanel } from './MaxFundingResultsPanel';
import { useMaxFundingCompute } from './useMaxFundingCompute';
import { useMaxFundingScenario } from './useMaxFundingScenario';
import {
  buildDefaultSubScenario,
  buildEmptyMaxFundingPayload,
  DEFAULT_RETIREMENT_AGE,
} from './maxFundingDefaults';
import { useUnsavedChanges } from './UnsavedChangesContext';

type MaxFundingScenarioPageProps = Readonly<{
  profileId: string;
  scenarioId: string | null;
  clientId: string | null;
  clients: Client[];
}>;

function nextRetirementAge(existing: number[]): number {
  const max = existing.length > 0 ? Math.max(...existing) : DEFAULT_RETIREMENT_AGE - 5;
  const candidate = max + 5;
  if (candidate > 75) return 75;
  if (candidate < 60) return 60;
  return candidate;
}

export function MaxFundingScenarioPage({
  profileId,
  scenarioId,
  clientId,
  clients,
}: MaxFundingScenarioPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve the client record for the new-in-memory mode so we can seed defaults.
  const seedClient = useMemo(
    () => clients.find((c) => c.id === clientId) ?? undefined,
    [clients, clientId],
  );

  const initialPayload = useMemo<MaxFundingPayload | undefined>(
    () => (scenarioId ? undefined : buildEmptyMaxFundingPayload(seedClient)),
    [scenarioId, seedClient],
  );

  const {
    scenario,
    payload,
    isLoading,
    error,
    saving,
    isDirty,
    setPayload,
    finalizeSave,
  } = useMaxFundingScenario({
    profileId,
    scenarioId,
    clientId: clientId ?? undefined,
    initialPayload,
  });

  const {
    setIsDirty,
    registerSaveHandler,
    registerDiscardHandler,
    registerNameProvider,
    requestNavigation,
    openSaveModal,
  } = useUnsavedChanges();

  const [activeIndex, setActiveIndex] = useState(0);

  const [selectedPensionsByTab, setSelectedPensionsByTab] = useState<
    Record<number, SelectedPension[]>
  >({});

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    return () => setIsDirty(false);
  }, [setIsDirty]);

  const subScenarios = payload?.scenarios ?? [];
  const activeScenario: MaxFundingSubScenario | undefined = subScenarios[activeIndex];
  const activeInputs: MaxFundingScenarioInputs | undefined = activeScenario?.inputs;

  const client = useMemo(
    () =>
      clients.find((c) => c.id === (scenario?.clientId ?? clientId)) ?? seedClient ?? clients[0],
    [clients, scenario?.clientId, clientId, seedClient],
  );
  const clientLabel =
    client && (client.firstName || client.lastName)
      ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim()
      : client?.email ?? 'Client';

  const emptyInputs: MaxFundingScenarioInputs = useMemo(
    () => ({
      retirementAge: DEFAULT_RETIREMENT_AGE,
      currentGrossPayeEarnings: 0,
      yearsWithCurrentEmployerToDate: 0,
      existingPension: false,
      defBenPrevTotal: 0,
      defConPrevTotal: 0,
      defBenCurTotal: 0,
      defConCurTotal: 0,
      gender: 'MALE',
      maritalStatus: 'SINGLE',
      dateOfBirth: '',
    }),
    [],
  );

  const computeResult = useMaxFundingCompute(activeInputs ?? emptyInputs);

  const commitPayload = (next: MaxFundingPayload) => {
    setPayload(next);
  };

  const handleInputsChange = (nextInputs: MaxFundingScenarioInputs) => {
    if (!activeScenario) return;
    const nextSubScenarios = subScenarios.map((s, idx) =>
      idx === activeIndex
        ? { ...s, inputs: nextInputs, retirementAge: nextInputs.retirementAge }
        : s,
    );
    commitPayload({ ...payload, scenarios: nextSubScenarios });
  };

  const handleAddTab = () => {
    const nextAge = nextRetirementAge(subScenarios.map((s) => s.retirementAge));
    const defaults = buildDefaultSubScenario(client, nextAge);
    const seeded: MaxFundingSubScenario = activeInputs
      ? {
          retirementAge: nextAge,
          inputs: { ...activeInputs, retirementAge: nextAge },
        }
      : defaults;
    const next = [...subScenarios, seeded];
    commitPayload({ ...payload, scenarios: next });
    setActiveIndex(next.length - 1);
  };

  const handleCloseTab = (idx: number) => {
    if (subScenarios.length <= 1) return;
    const next = subScenarios.filter((_, i) => i !== idx);
    commitPayload({ ...payload, scenarios: next });
    setActiveIndex(Math.min(activeIndex, next.length - 1));
  };

  const handleRenameTab = (idx: number, name: string | undefined) => {
    if (!subScenarios[idx]) return;
    const next = subScenarios.map((s, i) =>
      i === idx ? { ...s, name: name ?? undefined } : s,
    );
    commitPayload({ ...payload, scenarios: next });
  };

  const navigateBackToList = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.delete('scenarioId');
    next.delete('clientId');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const handleBackToList = useCallback(() => {
    requestNavigation(navigateBackToList);
  }, [requestNavigation, navigateBackToList]);

  useEffect(() => {
    registerSaveHandler(async (name: string) => {
      await finalizeSave(name);
      navigateBackToList();
    });
  }, [registerSaveHandler, finalizeSave, navigateBackToList]);

  useEffect(() => {
    registerDiscardHandler(async () => {
      navigateBackToList();
    });
  }, [registerDiscardHandler, navigateBackToList]);

  useEffect(() => {
    registerNameProvider(() => payload?.name ?? '');
  }, [registerNameProvider, payload?.name]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (error || !activeScenario) {
    return (
      <Alert variant="error" header="Error Loading Scenario">
        <p>{error ?? 'Scenario not found.'}</p>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-md" data-testid="max-funding-scenario-page">
      <div className="flex items-center justify-between bg-white !h-[80px] px-lg">
        <div className="flex items-center gap-md">
          <button
            type="button"
            onClick={handleBackToList}
            className="w-[48px] h-[48px] cursor-pointer flex items-center justify-center rounded-full bg-lilac-05 hover:bg-lilac-04 transition-colors text-black"
            aria-label="Back to scenarios list"
            data-testid="scenario-back-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14.8242 3.90332C14.919 3.80868 15.0821 3.80861 15.1768 3.90332C15.2713 3.99805 15.2714 4.16116 15.1768 4.25586L8.65625 10.7764C7.98158 11.4515 7.98157 12.5475 8.65625 13.2227L15.1768 19.7432C15.2697 19.8362 15.2704 19.9945 15.1807 20.0898C15.1152 20.1479 15.048 20.1699 15 20.1699C14.9356 20.1699 14.8754 20.1469 14.8242 20.0957L8.30371 13.5762C7.43897 12.7114 7.43897 11.2876 8.30371 10.4229L14.8242 3.90332Z" fill="#141414" stroke="#2D0A6C"/>
            </svg>
          </button>
          <div className="flex flex-col ">
            <h2 className="text-body-01 font-normal text-black font-sans leading-normal m-0">
              Maximum Funding Calculator
            </h2>
            <span className="text-body-03 font-normal text-black font-sans leading-normal">
              {clientLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-[12px]">
          <Button
            variant="filled"
            size="lg"
            onClick={openSaveModal}
            loading={saving}
            data-testid="scenario-save-button"
          >
            Save This Scenario
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between px-lg">
        <MaxFundingScenarioTabs
          retirementAges={subScenarios.map((s) => s.retirementAge)}
          names={subScenarios.map((s) => s.name)}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
          onAdd={handleAddTab}
          onClose={handleCloseTab}
          onRename={handleRenameTab}
          disabled={saving}
        />
        {/*
          Hidden until the Cashflow Model handoff is implemented (deferred v2):
          - new vs existing forecast modal
          - which calculation outputs to send
          - dates per output (filled on the Cashflow page)
          When ready, restore this <button> and wire it to the Cashflow API.

          <button
            type="button"
            disabled
            title="Available in v2"
            data-testid="send-to-cashflow-button"
            className="h-[40px] px-lg rounded-lg border border-purple-primary bg-white text-purple-primary font-sans text-body-02 font-medium leading-normal cursor-not-allowed disabled:opacity-60"
          >
            Send To Cashflow Model
          </button>
        */}
      </div>

      <div className="grid grid-cols-2 gap-md px-lg pb-lg">
        <MaxFundingScenarioForm
          inputs={activeScenario.inputs}
          onChange={handleInputsChange}
          profileId={profileId}
          clientId={client?.id ?? ''}
          selectedPensions={selectedPensionsByTab[activeIndex] ?? []}
          onSelectedPensionsChange={(next) =>
            setSelectedPensionsByTab((prev) => ({ ...prev, [activeIndex]: next }))
          }
          disabled={saving}
        />
        <MaxFundingResultsPanel
          outputs={computeResult.outputs}
          warnings={computeResult.warnings}
          isLoading={computeResult.isLoading}
          error={computeResult.error}
        />
      </div>
    </div>
  );
}
